import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Typography, Stack, Alert, Paper, Divider,
} from '@mui/material';
import { useSnackbar } from 'notistack';

import { formatCnic } from '../../../utils/cnic';
import dateTimeUtils from '../../../utils/dateTimeUtils';
import { admissionApplicationService } from '../../../services/admissions/admissionApplicationService';
import { AdmissionApplication } from '../../../types/admissions';

interface Props {
  open: boolean;
  application: AdmissionApplication;
  onCancel: () => void;
  onDone: () => Promise<void> | void;
}

/**
 * EnrollmentConfirmDialog — the "final profile check" step from Journey 1
 * Step 14. The officer walks the parent through the identity fields one
 * last time (chance to correct name spellings, father's name, phone, etc.)
 * before the handover controller writes the Student + Enrollment records.
 *
 * Phase 1 scope: no inline-edit of fields. The officer confirms the fields
 * are correct, OR aborts and uses the Family tab to fix any typo before
 * confirming. Change-diff capture + inline edit lands in a follow-up chunk.
 */

const EnrollmentConfirmDialog: React.FC<Props> = ({ open, application, onCancel, onDone }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [busy, setBusy] = useState(false);

  const close = () => { if (!busy) onCancel(); };

  const submit = async () => {
    setBusy(true);
    try {
      await admissionApplicationService.confirmForEnrollment(application._id);
      enqueueSnackbar('Enrollment confirmation recorded', { variant: 'success' });
      await onDone();
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Failed to confirm', { variant: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
      <DialogTitle component="div">
        <Typography variant="h6" fontWeight={700}>Confirm for enrollment</Typography>
        <Typography variant="caption" color="text.secondary">
          Last chance to correct spellings before the Student record is written.
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>
            <Typography variant="body2">
              Go through these details with the parent. If anything is wrong, close this and
              correct it on the Details tab first.
            </Typography>
          </Alert>

          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600, mb: 1.5, display: 'block' }}>
              Student profile
            </Typography>
            <Stack spacing={1}>
              <Row k="Name"            v={application.name} />
              <Row k="Father's name"   v={application.fatherName} />
              <Row k="Father's phone"  v={application.fatherPhone} />
              <Row k="Address"         v={application.address} />
              <Row k="CNIC / B-Form"   v={formatCnic(application.cnic)} mono />
              {application.dob && <Row k="Date of birth" v={dateTimeUtils.formatDateForDisplay(application.dob)} />}
              {application.gender && <Row k="Gender" v={application.gender} capitalize />}
            </Stack>
            <Divider sx={{ my: 2 }} />
            <Typography variant="caption" color="text.secondary">
              These details are copied to the student's record when they are enrolled.
            </Typography>
          </Paper>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={close} disabled={busy}>Cancel</Button>
        <Button
          variant="contained"
          color="success"
          disableElevation
          onClick={submit}
          disabled={busy}
        >
          {busy ? 'Confirming…' : 'Everything looks correct — confirm'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const Row: React.FC<{ k: string; v: string; mono?: boolean; capitalize?: boolean }> = ({ k, v, mono, capitalize }) => (
  <Stack direction="row" spacing={2}>
    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140, flexShrink: 0 }}>{k}</Typography>
    <Typography
      variant="body2"
      sx={{
        fontFamily: mono ? 'monospace' : undefined,
        textTransform: capitalize ? 'capitalize' : undefined,
        fontWeight: 500,
      }}
    >
      {v || '—'}
    </Typography>
  </Stack>
);

export default EnrollmentConfirmDialog;
